import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { WrenchIcon } from '@phosphor-icons/react';
import {
  isGlobalOnly,
  type FeatureDef,
  type FeatureOverrideState,
} from '@logspot-platform/platform-types';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { SectionHeader } from '@/components/section-header';
import { adminGlobalFeaturesOptions } from '@/hooks/query-options';
import { FeaturesService } from '@/api/features-service';
import type { AdminGlobalFeatures } from '@/api/features-service';
import {
  LaunchFlagsCard,
  StrandedOverridesCard,
} from '@/components/super-admin/launch-flags';

export const Route = createFileRoute('/_auth/_side-layout/super-admin/features')(
  {
    component: SuperAdminFeaturesPage,
    beforeLoad: async ({ context }) => {
      if (context.platformRole !== 'admin') {
        throw redirect({ to: '/' });
      }
    },
  },
);

// One ops feature as a plain On/Off switch (no Inherit; the global level is the
// source, so On/Off is the whole story).
function OpsToggle({
  feature,
  override,
  onSaved,
}: {
  feature: FeatureDef;
  override?: FeatureOverrideState;
  onSaved: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  // Effective global value: the '*' override, or the code default when unset.
  const checked = override?.value ?? feature.defaults.FREE;

  const setValue = async (value: boolean) => {
    setPending(true);
    try {
      await FeaturesService.setFeatureOverride({ featureKey: feature.key, value });
      onSaved();
      toast.success(`${feature.label} turned ${value ? 'on' : 'off'}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="border-border flex items-start justify-between gap-4 rounded-md border p-3">
      <div className="min-w-0">
        <p className="font-medium">{feature.label}</p>
        <p className="text-muted-foreground text-sm">{feature.description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={(value) =>
          value ? setValue(true) : setConfirmOff(true)
        }
        disabled={pending}
        aria-label={feature.label}
        className="mt-0.5 shrink-0"
      />
      <ConfirmModal
        open={confirmOff}
        title={`Turn Off ${feature.label}?`}
        message={`${feature.label} will be off for all organizations until you turn it back on.`}
        variant="destructive"
        confirmText="Turn Off"
        onFinish={async () => {
          await setValue(false);
          setConfirmOff(false);
        }}
        onCancel={() => setConfirmOff(false)}
      />
    </div>
  );
}

// Global on/off for ops toggles (provider kill-switches that stay in code, demo)
// and the LTD promo. Not customer launch flags.
function OpsToggles() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(adminGlobalFeaturesOptions());
  const global = data as AdminGlobalFeatures | undefined;

  const opsFeatures = (global?.features ?? []).filter(isGlobalOnly);

  const onSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-features', 'global'] });
    // The current-org effective flags may depend on a global default.
    queryClient.invalidateQueries({ queryKey: ['features'] });
  };

  return (
    <Card>
      <CardContent className="space-y-4">
        <SectionHeader
          icon={<WrenchIcon size={20} />}
          title="Ops & Toggles"
          subTitle="Global on/off for provider kill-switches, demo, and the lifetime-deal promo. Not customer launch flags."
        />
        {isLoading || !global ? (
          <p className="text-muted-foreground py-4 text-sm">Loading...</p>
        ) : opsFeatures.length === 0 ? (
          <p className="text-muted-foreground py-4 text-sm">No ops toggles.</p>
        ) : (
          <div className="space-y-3">
            {opsFeatures.map((feature) => (
              <OpsToggle
                key={feature.key}
                feature={feature}
                override={global.global[feature.key]}
                onSaved={onSaved}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SuperAdminFeaturesPage() {
  return (
    <>
      <PageHeader
        title="Feature Flags"
        description="Launch customer features, manage ops toggles, and clear stale overrides. Per-customer overrides are managed from the Subscriptions page."
      />
      <PageContent>
        <LaunchFlagsCard />
        <OpsToggles />
        <StrandedOverridesCard />
      </PageContent>
    </>
  );
}
